/**
 * Purpose: Term related selectors, in particular those that use `getSelectedTerm`.
 */

// libraries
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';
import type {
    CashOption,
    OfferType,
    PaymentProfitDetails,
    PaymentSchedule,
    paymentServicesTypes,
    PaymentTerm,
    ResidualValueType,
    BalloonType,
    BalloonTypeDxg
} from '@makemydeal/dr-platform-types';
import { CASH, PaymentScheduleTypes, SpecialPaymentFormula } from '@makemydeal/dr-platform-types';
import { createSelector } from 'reselect';

// utils
import { parseTermId } from '../utils/termIdParser';
import { getSliceData } from './helpers';
import { getTypedBuyRates } from '../utils/typedBuyRate';
import {
    getCurrentOfferType,
    getIsUserProgramQuotes,
    getOfferDetails,
    getOfferTerm,
    getSelectedTerm,
    getSelectedTermByOfferType,
    getTermByOfferTypeAndMonths,
    getTermIdByOfferType,
    isAspPaymentGridRedesignEnabled
} from './offerNodeSelectors';
import { isOnePayLeaseFrequency } from './utils/nonSelectorUtils';

export const getTermLength = (state: OfferReduxBaseState): number | null => {
    const offerType: OfferType = getCurrentOfferType(state);
    return offerType === CASH ? null : getSelectedTerm(state)?.months;
};

export const getBuyRate = (state: OfferReduxBaseState, enableFallback: boolean = true): number | undefined => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.buyRate || (enableFallback ? 0 : undefined);
};

export const getSellRate = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.sellRate || 0;
};

export const getRateType = (state: OfferReduxBaseState) => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.rateType || '';
};

export const getLenderLegalName = (state: OfferReduxBaseState): string => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.lenderLegalName || '';
};

export function getMinimumCashDown(state: OfferReduxBaseState): number {
    // uses same logic as getMonthlyPayment() to get current payment's minimum due at signing
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.minimumCashDown || 0;
}

export function getMinimumCashDownSummary(state: OfferReduxBaseState) {
    // uses same logic as getMonthlyPayment() to get current payment's minimum due at signing
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm.minimumCashDownSummary || [];
}

export function getNetCashDown(state: OfferReduxBaseState, offerType?: OfferType): number {
    return getSelectedTerm(state, offerType)?.netCashDown || 0;
}

export function getCapReductionRebate(state: OfferReduxBaseState): number {
    return getSelectedTerm(state)?.capReductionRebate || 0;
}

export function getCapReductionTradeIn(state: OfferReduxBaseState): number {
    return getSelectedTerm(state)?.capReductionTradeIn || 0;
}

export const getApr = (state: OfferReduxBaseState, offerType?: OfferType): number => {
    const selectedTerm = getSelectedTerm(state, offerType);
    return selectedTerm?.apr || 0;
};

export const getAdjustedResidualValue = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.adjustedResidualValue || 0;
};

export const getAdjustedResidualPercent = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.adjustedResidualPercent || 0;
};

export const getResidualType = (state: OfferReduxBaseState): ResidualValueType | undefined => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.residualType;
};

export const getAdjustedResidualValueByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number => {
    const selectedTerm = getSelectedTerm(state, offerType);
    return selectedTerm?.adjustedResidualValue || 0;
};

export const getAdjustedResidualPercentByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number => {
    const selectedTerm = getSelectedTerm(state, offerType);
    return selectedTerm?.adjustedResidualPercent || 0;
};

/**
 * @deprecated Please use getAdjustedResidualPercent
 */
export const getEffectiveResidualPercentage = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.effectiveResidualPercentage || 0;
};

export const getCapCostTotal = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.capCostTotal || 0;
};

export const getTotalMiles = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.totalMiles || 0;
};

export const getPaymentFrequency = (state: OfferReduxBaseState, skipFallback: boolean = false): PaymentSchedule | undefined => {
    const selectedTerm = getSelectedTerm(state);
    const frequency = selectedTerm?.paymentFrequency as PaymentSchedule | undefined;
    if (frequency || skipFallback) {
        return frequency;
    }
    return PaymentScheduleTypes.Monthly;
};

export const isOnePayLease = (state: OfferReduxBaseState): boolean => {
    const paymentFrequency = getPaymentFrequency(state);
    return isOnePayLeaseFrequency(paymentFrequency);
};

export const getMaxResidualizedMsrp = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.maxResidualizedMsrp || 0;
};

export const getMaxResidualizedMsrpApplied = (state: OfferReduxBaseState): boolean => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.maxResidualizedMsrpApplied || false;
};

export const getAdjustedCapCost = (state: OfferReduxBaseState, offerType?: OfferType): number => {
    const selectedTerm = getSelectedTerm(state, offerType);
    return selectedTerm?.adjustedCapCost || 0;
};

export const getCapitalizedTaxTotal = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.capitalizedTaxTotal || 0;
};

export const getMessagesFromCurrentTerm = (state: OfferReduxBaseState): paymentServicesTypes.Message[] => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.messages || [];
};

export const getBaseResidualDollar = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.baseResidualDollar || 0;
};

export const getBalloonAmount = (state: OfferReduxBaseState): number => {
    return getBaseResidualDollar(state);
};

export const getBalloonTerm = (state: OfferReduxBaseState): number | undefined => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.balloonTerm;
};

export const getBalloonType = (state: OfferReduxBaseState): BalloonType | BalloonTypeDxg | undefined => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.balloonType;
};

export const getBalloonTermByType = (state: OfferReduxBaseState): number | undefined => {
    const selectedTerm = getSelectedTerm(state);
    const balloonTerm = getBalloonTerm(state);
    const balloonType = getBalloonType(state);
    if (balloonTerm) return balloonTerm;
    return balloonType === 'term-1' ? selectedTerm?.months - 1 : selectedTerm?.months;
};

export const getBalloonExpiration = (state: OfferReduxBaseState): string => {
    const { balloonExpiration } = getSelectedTerm(state);
    if (balloonExpiration) {
        return balloonExpiration.includes('T') ? balloonExpiration : balloonExpiration + 'T12:00:00.000Z'; // Convert to mid day UTC if value is only date eg. '2025-12-31'
    }
    return '';
};

export const getBaseResidualPercent = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.baseResidualPercent || 0;
};

/**
 * The user's program residual override for the selected term, or undefined when there is none.
 *
 * Mirrors `getSellRateOverride` below: under user-program-quotes the deal-level override is the one the
 * payment payload reads, and under program-quotes it lives on the selected term in `overridesByTerm`.
 */
export const getProgramResidualOverrideForSelectedTerm = (
    state: OfferReduxBaseState,
    key: 'programResidual' | 'programResidualPercent'
): number | undefined => {
    const offerType = getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, offerType);

    if (getIsUserProgramQuotes(state, offerType)) {
        return key === 'programResidual' ? offerDetails?.programResidualOverride : offerDetails?.programResidualPercentOverride;
    }

    const months = getSelectedTerm(state)?.months;
    return offerDetails?.overridesByTerm?.[months!]?.residualInfo?.[key];
};

/**
 * Program residual for the selected term, preferring the user's override over the last payment response.
 *
 * The override has to win for the same reason it does for sell rate: the payment grid, the deal screen's
 * Base Residual field and the Residual Breakdown tab all read this, so reading only the response would
 * leave the two deal-screen fields showing a stale number until a payment came back. Gated on the payment
 * grid redesign toggle so behaviour without it is unchanged.
 */
export const getProgramResidualPercent = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    if (isAspPaymentGridRedesignEnabled(state)) {
        const override = getProgramResidualOverrideForSelectedTerm(state, 'programResidualPercent');
        if (override != null) return override;
    }
    return selectedTerm?.programResidualPercent || 0;
};

export const getProgramResidual = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    if (isAspPaymentGridRedesignEnabled(state)) {
        const override = getProgramResidualOverrideForSelectedTerm(state, 'programResidual');
        if (override != null) return override;
    }
    return selectedTerm?.programResidual || 0;
};

export const getBaseMonthlyPayment = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.baseMonthlyPayment || 0;
};

export const getBaseMonthlyPaymentTotal = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.baseMonthlyPaymentsTotal || 0;
};

export const getTotalDepreciation = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.totalDepreciation || 0;
};

export const getPeriodicDepreciation = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.periodicDepreciation || 0;
};

export const getTotalInterest = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.totalInterest || 0;
};

export const getMonthlyRentCharged = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.rentCharged || 0;
};

export const getTotalAmountDue = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.totalAmountDue || 0;
};

export const getUpfrontAddonsTotal = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.upfrontAddonsTotal || 0;
};

export const getUpfrontFeesTotal = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.upfrontFeesTotal || 0;
};

export const getUpfrontTaxTotal = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.upfrontTaxTotal || 0;
};

export const getMonthlyUseTax = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return (selectedTerm?.mutTaxAmount as number) || selectedTerm?.taxBreakdown?.monthlyUseTotal || 0;
};

export const getAdditionalMonthlyTaxAmount = (state: OfferReduxBaseState): number | undefined => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.additionalMonthlyTaxAmount;
};

export const getSelectedPaymentProfitDetails = (state: OfferReduxBaseState): PaymentProfitDetails | undefined => {
    const offerInputs = getOfferTerm(state);
    return offerInputs?.profit;
};

export const getPriceWithAdds = (state: OfferReduxBaseState, offerType?: OfferType): number => {
    const selectedTerm = getOfferTerm(state, offerType);
    return selectedTerm?.priceWithAdds ?? 0;
};

export const getTotalOfBasePayments = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.totalOfBasePayments ?? 0;
};

export const getTotalOfPaymentsAndConsideration = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined => {
    const selectedTerm = getSelectedTerm(state, offerType);
    return selectedTerm?.totalOfPaymentsAndConsideration;
};

export const getAddsToCapCostTotal = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.addsToCapCostTotal ?? 0;
};

export const getCashOffsetAmountAddedToPrice = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.cashOffsetAmountAddedToPrice ?? 0;
};

export const getPriorLoanBalance = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.priorLoanBalance ?? 0;
};

export const getPriorLeaseBalanceAmount = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.priorLeaseBalance ?? 0;
};

/**
 * @deprecated Please use 'getPriorLoanBalance' instead.
 * Payment Services removed cashOffsetAmountAddedToPrice from returned total
 */
export const getCashOffsetPriorLoanBalance = (state: OfferReduxBaseState): number => {
    const cashOffsetAmountAddedToPrice = getCashOffsetAmountAddedToPrice(state);
    const priorLoanBalance = getPriorLoanBalance(state);

    return priorLoanBalance > 0 ? cashOffsetAmountAddedToPrice + priorLoanBalance : cashOffsetAmountAddedToPrice;
};

export const getCapitalizedLenderFees = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.capitalizedLenderFees ?? 0;
};

export const getCapitalizedGovFees = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.capitalizedGovFees ?? 0;
};

export const getCapitalizedDealerFees = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.capitalizedDealerFees ?? 0;
};

export const getCapitalizedTaxes = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.capitalizedTaxes ?? 0;
};

export const getCapitalizedAccessories = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.capitalizedAccessories ?? 0;
};

export const getCapitalizedProtectionProducts = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.capitalizedProtectionProducts ?? 0;
};

export const getSalePriceWithAddsToCapCost = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.salePriceWithAddsToCapCost ?? 0;
};

export const getTotalAddOns = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.totalAddOns ?? 0;
};

export const getTotalLenderFees = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.totalLenderFees ?? 0;
};

export const getTradeDownAppliedToUpfrontItems = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.tradeDownAppliedToUpfrontItems ?? 0;
};

export const getCashAppliedToUpfrontItems = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.cashAppliedToUpfrontItems ?? 0;
};

export const getRebateAppliedToUpfrontItems = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.rebateAppliedToUpfrontItems ?? 0;
};

export const getDealerCashAppliedToUpfrontItems = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.dealerCashAppliedToUpfrontItems ?? 0;
};

export const getDiaLenderFees = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.diaLenderFees ?? 0;
};

export const getDiaGovFees = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.diaGovFees ?? 0;
};

export const getDiaDealerFees = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.diaDealerFees ?? 0;
};

export const getDiaTaxes = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.diaTaxes ?? 0;
};

export const getDiaAccessories = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.diaAccessories ?? 0;
};

export const getDiaProtectionProducts = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.diaProtectionProducts ?? 0;
};

export const getCashDueFromCustomer = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.cashDueFromCustomer ?? 0;
};

export const getTradeDown = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.tradeDown ?? 0;
};

export const getRebate = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.rebate ?? 0;
};

export const getTotalProductResidual = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.totalProductResidual ?? 0;
};

export const getTotalAccessoriesResidual = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.totalAccessoriesResidual ?? 0;
};

/**
 * Gets the sell rate from the selected term by offer type.
 * @param state redux state
 * @param offerType offer type to lookup
 * @returns sell rate value
 */
export const getSellRateByOfferType = (state: OfferReduxBaseState, offerType: OfferType, term?: number): number => {
    if (term) {
        const termObject = getTermByOfferTypeAndMonths(state, offerType, term);
        return termObject?.sellRate ?? 0;
    }
    const selectedTerm = getSelectedTermByOfferType(state, offerType);
    return selectedTerm?.sellRate ?? 0;
};

/**
 *
 * @param state redux state
 * @param offerType Offer type
 * @param enableFallback (default: false), Pass true to enable a term value lookup when override is unavailable.
 * @param enableMonthLookup (default: false), used for getting sell rate for the input term and not selected term.
 * @returns sell rate
 */
export const getSellRateOverrideByOfferTypeAndTerm = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    months: number | undefined,
    enableFallback = false,
    enableMonthLookup = false
): number | undefined => {
    const offerDetails = getOfferDetails(state, offerType);
    const sellRateOverride = offerDetails?.overridesByTerm?.[months!]?.sellRate;
    if (sellRateOverride != null) return sellRateOverride;
    if (!enableFallback) return undefined;
    const sellRateFromTerm = enableMonthLookup
        ? getSellRateByOfferType(state, offerType, months)
        : getSellRateByOfferType(state, offerType);
    return sellRateFromTerm;
};

/**
 * Get the sell rate override
 * @param state redux state
 * @param enableFallback (default: false), Pass true to enable a term value lookup when override is unavailable.
 * @returns sell rate value
 */
export const getSellRateOverride = (
    state: OfferReduxBaseState,
    enableFallback = false,
    offerType?: OfferType
): number | undefined => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const isUserProgramQuotes = getIsUserProgramQuotes(state, currentOfferType);

    if (isUserProgramQuotes) {
        const offerDetails = getOfferDetails(state, currentOfferType);
        return offerDetails.sellRateOverride;
    } else {
        const selectedTerm = getSelectedTerm(state);
        return getSellRateOverrideByOfferTypeAndTerm(state, currentOfferType, selectedTerm?.months, enableFallback);
    }
};

/**
 * Gets the buy rate from the selected term by offer type.
 * @param state redux state
 * @param offerType offer type to lookup
 * @returns buy rate value
 */

export const getBuyRateByOfferType = (state: OfferReduxBaseState, offerType: OfferType, enableFallback: boolean = true): number => {
    const term = getSelectedTermByOfferType(state, offerType);
    return term?.buyRate ?? (enableFallback ? 0 : undefined);
};

/**
 *
 * @param state redux state
 * @param offerType Offer type
 * @param enableFallback (default: false), Pass true to enable a term value lookup when override is unavailable.
 * @returns buy rate
 */
export const getBuyRateOverrideByOfferTypeAndTerm = (
    state: OfferReduxBaseState,
    offerType?: OfferType,
    termMonths?: number,
    enableFallback: boolean = false
): number | undefined => {
    const type = offerType || getCurrentOfferType(state);
    const months = termMonths || getTermLength(state);
    const offerDetails = getOfferDetails(state, type);
    const rateOverride = offerDetails?.overridesByTerm?.[months!]?.buyRate;
    if (rateOverride != null) return rateOverride;
    if (!enableFallback) return undefined;
    const rateFromTerm = getBuyRateByOfferType(state, type, enableFallback);
    return rateFromTerm;
};

export const getTypedBuyRateByOfferTypeAndTerm = (
    state: OfferReduxBaseState,
    offerType?: OfferType,
    termMonths?: number
): number | undefined => {
    const type = offerType || getCurrentOfferType(state);
    const months = termMonths || getTermLength(state);
    return getTypedBuyRates(getOfferDetails(state, type))[months!];
};

/**
 * The stored per-term buy rate, but only while it is the value the user typed. Other code writes the same slot (the grid
 * copies the sell rate into it), and a rate nobody typed must not reach program-quotes. The Buy Rate field and the
 * payment payload both read it, so what the field shows is what the request sends.
 */
export const getTypedBuyRateOverrideByOfferTypeAndTerm = (
    state: OfferReduxBaseState,
    offerType?: OfferType,
    termMonths?: number
): number | undefined => {
    const stored = getBuyRateOverrideByOfferTypeAndTerm(state, offerType, termMonths);
    return stored != null && stored === getTypedBuyRateByOfferTypeAndTerm(state, offerType, termMonths) ? stored : undefined;
};

/**
 * Get the buy rate override
 * @param state redux state
 * @param enableFallback (default: false), Pass true to enable a term value lookup when override is unavailable.
 * @returns buy rate value
 */
export const getBuyRateOverride = (
    state: OfferReduxBaseState,
    enableFallback = false,
    offerType?: OfferType
): number | undefined => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const isUserProgramQuotes = getIsUserProgramQuotes(state, currentOfferType);

    if (isUserProgramQuotes) {
        const offerDetails = getOfferDetails(state, currentOfferType);
        return offerDetails.buyRateOverride;
    } else {
        const selectedTerm = getSelectedTerm(state);
        return getBuyRateOverrideByOfferTypeAndTerm(state, currentOfferType, selectedTerm?.months, enableFallback);
    }
};

export const getExcessMileageCharge = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return (selectedTerm?.excessMileageCharge as number) || 0;
};

export const getSelectedTermMonths = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined => {
    const offer = offerType ?? getCurrentOfferType(state);
    const termId = getTermIdByOfferType(state, offer);
    if (!termId) {
        return undefined;
    }

    const idParams = parseTermId(termId);

    return idParams.termIdParams?.term;
};

export const getSelectedTermAmountFinanced = (state: OfferReduxBaseState, offerType?: OfferType) => {
    const selectedTerm = getSelectedTerm(state, offerType);
    return selectedTerm?.amountFinanced || 0;
};

export const getSelectedTermTotalInterest = (state: OfferReduxBaseState, offerType?: OfferType) => {
    const selectedTerm = getSelectedTerm(state, offerType);
    return selectedTerm?.totalInterest || 0;
};

export const getSelectedTermBaseMonthlyPaymentsTotal = (state: OfferReduxBaseState, offerType?: OfferType) => {
    const selectedTerm = getSelectedTerm(state, offerType);
    return selectedTerm?.baseMonthlyPaymentsTotal || 0;
};

export const getRateTypeByTerm = (state: OfferReduxBaseState, offerType: OfferType, months: number) => {
    // DE747870: resolve the grid label from the same penciled source as the value — the offer-level
    // rateTypeOverride when present, else the byId term's rateType.
    //
    // NOTE US2075533: the per-term rate type stamp is deliberately NOT read here. This selector also builds the
    // outgoing payment request (`paymentPayloadUtils.getPaymentGridOverridesByTerm`), where a term node's single
    // `rateType` must describe a `sellRate` and a `buyRate` that can each have fallen back to the deal-level
    // override — one stamp cannot label both. The stamp is display-only: see `getGridRateTypeByTerm` in
    // `paymentGridSelectors`.
    const offerDetails = getOfferDetails(state, offerType);
    const term = getTermByOfferTypeAndMonths(state, offerType, months);
    return (offerDetails.rateTypeOverride || term?.rateType) as paymentServicesTypes.RateType;
};

export const getFirstMonthPayment = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.monthlyPayment ?? 0;
};

export const getOutOfPocket = (
    state: OfferReduxBaseState,
    offerType?: OfferType,
    paymentTerm?: PaymentTerm
): number | undefined => {
    if (paymentTerm == null) {
        const type = offerType || getCurrentOfferType(state);
        const selectedTerm = getSelectedTerm(state, type);
        return getTermOutOfPocket(selectedTerm);
    } else {
        return getTermOutOfPocket(paymentTerm);
    }
};

export const getTermOutOfPocket = (term: PaymentTerm): number | undefined => {
    return term?.outOfPocket;
};

export const getSpecialPaymentFormula = (state: OfferReduxBaseState): SpecialPaymentFormula | undefined => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.specialPaymentFormula;
};

/**
 * @description This selector get the special payment formula from the selected term `offer.terms.byId[termId].specialPaymentFormula`
 */
export const isFordFlexBuyDeal = (state: OfferReduxBaseState): boolean => {
    return getSpecialPaymentFormula(state) === SpecialPaymentFormula.FordFlexBuy;
};

// must be moved to feature Toggle selectors
export const isTargetOutOfPocketLeaseEnabled = (state: OfferReduxBaseState): boolean =>
    !!state?.featureToggles?.enableTargetOutOfPocketLease;
export const isDealSummaryLayoutV2Enabled = (state: OfferReduxBaseState): boolean => !!state?.featureToggles?.dealSummaryLayoutV2;
export const isDe771200DueAtSigningFixEnabled = (state: OfferReduxBaseState): boolean =>
    !!state?.featureToggles?.enablefix_DE771200;
//

export const getFordFlexBuyFields = (_state: OfferReduxBaseState): (keyof PaymentTerm)[] => {
    return ['initialPaymentDiscount', 'firstTermPaymentDetail', 'secondTermPaymentDetail', 'comparableTermPaymentDetail'];
};

export const getFordFlexBuyData = (
    state: OfferReduxBaseState,
    fields: (keyof PaymentTerm)[] = getFordFlexBuyFields(state)
): Partial<PaymentTerm> | undefined => {
    if (!isFordFlexBuyDeal(state)) {
        return undefined;
    }

    const selectedTerm = getSelectedTerm(state);

    return getSliceData(selectedTerm, fields, true);
};

export const getDiscount = (state: OfferReduxBaseState, offerType?: OfferType): number => {
    const term = getOfferTerm(state, offerType);
    return term.discount || 0;
};

export const getDiscountByTerm = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.discount || 0;
};

export const getMonthsByTerm = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.months || 0;
};

export const getNetTradeByTerm = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.netTrade || 0;
};

export const getDueAtSigningByTerm = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.dueAtSigning || 0;
};

export const getNetCashPayout = (state: OfferReduxBaseState): number => {
    const term = getOfferTerm(state);
    return term?.netCashPayout || 0;
};

export const getCreditTierByTerm = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.creditTier || 0;
};

export const getCapReductionTaxCapitalizedIndicator = (state: OfferReduxBaseState): boolean => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.capReductionTaxCapitalizedIndicator ?? false;
};

export const getFirstPaymentCapitalized = (state: OfferReduxBaseState): boolean | undefined => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.firstPaymentCapitalized;
};

export const getCashOption = (state: OfferReduxBaseState): CashOption | undefined => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.cashOption;
};

export const getTotalTaxReduction = (state: OfferReduxBaseState): number | undefined => {
    const offerType = getCurrentOfferType(state);
    if (offerType === CASH) {
        return getOfferDetails(state, CASH).totalTaxReduction;
    }
    return getSelectedTerm(state).totalTaxReduction;
};

export const getTradeInItems = (state: OfferReduxBaseState): any[] => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.tradeInItems || [];
};

export const getFirstMonthlyPayment = (state: OfferReduxBaseState): number => {
    const selectedTerm = getSelectedTerm(state);
    return selectedTerm?.firstPayment ?? 0;
};

export function getAnnualMiles(state: any): number {
    const termDetails = getSelectedTerm(state);
    return termDetails?.annualMiles || getAnnualMilesByTerm(state);
}

export function getDiaDueAtSigning(state: any) {
    const term = getSelectedTerm(state);
    return term?.diaDueAtSigning ?? 0;
}

export function getPaidAtSigning(state: any) {
    const term = getSelectedTerm(state);
    return term?.paidAtSigning ?? 0;
}

export type LeaseDueAtSigningTerm = PaymentTerm & {
    totalUpfrontItems?: number;
    cashOffSet?: number;
    cashOffSetAddedToPrice?: boolean;
};

const getLeaseDueAtSigningTerm = (state: OfferReduxBaseState, offerType?: OfferType): LeaseDueAtSigningTerm | undefined =>
    getSelectedTerm(state, offerType) as LeaseDueAtSigningTerm | undefined;

export const isLeaseDueAtSigningSectionEnabled = (state: OfferReduxBaseState): boolean =>
    !!state?.featureToggles?.F320813_Enable_Lease_Due_At_Signing_Section && !!state?.featureToggles?.enableEquityAllocationOrder;

export const getPaidAtSigningByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.paidAtSigning;

export const getDiaDueAtSigningByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.diaDueAtSigning;

export const getDueAtSigningByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.dueAtSigning;

export const getCashDueFromCustomerByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.cashDueFromCustomer;

export const getFirstPaymentByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.firstPayment;

export const getFirstPaymentCapitalizedByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): boolean | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.firstPaymentCapitalized;

export const getDiaTaxesByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.diaTaxes;

export const getDiaProtectionProductsByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.diaProtectionProducts;

export const getDiaAccessoriesByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.diaAccessories;

export const getUpfrontPriorLeaseBalanceByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number => {
    const term = getLeaseDueAtSigningTerm(state, offerType);
    return term?.priorLeaseBalanceMethod === 'Upfront' ? term.priorLeaseBalance ?? 0 : 0;
};

export const getCapitalizedPriorLeaseBalanceByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number => {
    const term = getLeaseDueAtSigningTerm(state, offerType);
    return term?.priorLeaseBalanceMethod === 'Capitalize' ? term.priorLeaseBalance ?? 0 : 0;
};

export const getSecurityDepositByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.securityDeposit;

export const getCapCostReductionByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined => {
    const capCostReduction = getLeaseDueAtSigningTerm(state, offerType)?.capCostReduction;
    return capCostReduction == null ? undefined : Number(capCostReduction);
};

export const getCashOffsetAmountAddedToPriceByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.cashOffsetAmountAddedToPrice;

export const getCashOffSetAddedToPriceByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): boolean | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.cashOffSetAddedToPrice;

export const getTotalUpfrontItemsByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.totalUpfrontItems;

export const getRebateByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.rebate;

export const getTradeDownByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.tradeDown;

export const getNetTradeByOfferType = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getLeaseDueAtSigningTerm(state, offerType)?.netTrade;

/**
 * @deprecated Use getAnnualMiles from selectedTermSelectors.ts instead
 */
export function getAnnualMilesByTerm(state: any): number {
    const currentOfferType = getCurrentOfferType(state);
    const term = getSelectedTerm(state, currentOfferType);
    const termInYears = term.months / 12;
    return term.totalMiles ? Math.round(term.totalMiles / termInYears) : 0;
}

export function getSelectedMonthlyPayment(state: any): number {
    const selectedTerm = getOfferTerm(state);
    return selectedTerm.monthlyPayment || 0;
}

export const recalculateCashAmount = createSelector(
    [getTotalAmountDue, getTotalOfBasePayments, getSelectedMonthlyPayment],
    (totalAmountDue, totalOfBasePayments, monthlyPayment) => {
        return totalAmountDue - totalOfBasePayments + monthlyPayment;
    }
);
